import {
  APISlideAttributes,
  ArdoqId,
  ViewIds,
  isMetamodelSlide,
  isReportSlide,
  isVisualizationSlide,
} from '@ardoq/api-types';
import { PayloadLoadSlide } from '../../presentation/viewPane/actions';
import { getCurrentUserPermissionContext } from 'streams/currentUserPermissions/currentUserPermissionContext$';
import { slides$ } from 'streams/slides/slides$';
import {
  slideOperations,
  type SlideInfo,
} from 'streams/slides/slideOperations';
import { ContextSort } from '@ardoq/data-model';

const getSlideById = (slideId: ArdoqId): APISlideAttributes | undefined =>
  slides$.state.byId[slideId];

const getViewId = (slideId: ArdoqId): ViewIds | undefined => {
  const slide = getSlideById(slideId);
  if (!slide) {
    return undefined;
  }
  return slideOperations.getSlideViewId(slide);
};

const getSlideInfo = (slideId: ArdoqId): SlideInfo | null => {
  const slide = getSlideById(slideId);
  if (!slide) {
    return null;
  }
  return slideOperations.getSlideInfo(slide, getCurrentUserPermissionContext());
};

const getSlideLoadPayload = (slideId: ArdoqId): PayloadLoadSlide | null => {
  const slide = getSlideById(slideId);
  if (!slide) {
    return null;
  }
  return slideOperations.toSlideLoadPayload(
    slide,
    getCurrentUserPermissionContext()
  );
};

const exists = (slideId: ArdoqId): boolean => Boolean(getSlideById(slideId));

const getDescription = (slideId: ArdoqId): string | undefined =>
  getSlideById(slideId)?.description;

const isViewpointSlide = (slideId: ArdoqId): boolean => {
  const slide = getSlideById(slideId);
  return slide ? slideOperations.isViewpointSlide(slide) : false;
};

const getSort = (slideId: ArdoqId): ContextSort | undefined => {
  const slide = getSlideById(slideId);
  if (!slide || !isVisualizationSlide(slide)) return;
  return slideOperations.getSort(slide);
};

const isVisualizationSlideProxy = (slideId: ArdoqId): boolean => {
  const slide = getSlideById(slideId);
  return Boolean(slide && isVisualizationSlide(slide));
};

const isReportSlideProxy = (slideId: ArdoqId): boolean => {
  const slide = getSlideById(slideId);
  return Boolean(slide && isReportSlide(slide));
};

const isMetamodelSlideProxy = (slideId: ArdoqId): boolean => {
  const slide = getSlideById(slideId);
  return Boolean(slide && isMetamodelSlide(slide));
};

/**
 * `slideInterface` snapshots `slides$.state` at call time.
 *
 * The app is stream-based now. Join `slides$` (`streams/slides/slides$.ts`,
 * emitting `{ list, byId }`) at a permitted boundary -- a `*$.ts` file, a file
 * inside a `*$/` folder, or a `*routines.ts` -- and pass the resolved slide to the
 * pure `slideOperations` (`streams/slides/slideOperations.ts`). In routines,
 * resolve the slide once and thread it through the pipe rather than re-issuing the
 * lookup at each step.
 *
 * Every member below is deprecated. See
 * `packages/eslint-plugin-ardoq-lint/docs/rules/no-model-interface-call.md`
 * for the hoisting recipe and the interface-to-stream substitution table.
 */
export const slideInterface = {
  /**
   * @deprecated Read `slides$.byId[slideId]` for presence.
   */
  exists,
  /**
   * @deprecated Read `slides$.byId[slideId]?.description`.
   */
  getDescription,
  /**
   * @deprecated Read `slides$.byId[slideId]` and call `slideOperations.getSlideViewId(slide)`.
   */
  getViewId,
  /**
   * @deprecated Read `slides$.byId[slideId]` and build the payload from the resolved slide in a pure helper.
   */
  getSlideLoadPayload,
  /**
   * @deprecated Read `slides$.byId[slideId]` and call `slideOperations.getSlideInfo(slide)`.
   */
  getSlideInfo,
  /**
   * @deprecated Read `slides$.byId[slideId]` and apply the pure `isVisualizationSlide` predicate from `@ardoq/api-types`.
   */
  isVisualizationSlide: isVisualizationSlideProxy,
  /**
   * @deprecated Read `slides$.byId[slideId]` and apply the pure `isReportSlide` predicate from `@ardoq/api-types`.
   */
  isReportSlide: isReportSlideProxy,
  /**
   * @deprecated Read `slides$.byId[slideId]` and apply the pure `isMetamodelSlide` predicate from `@ardoq/api-types`.
   */
  isMetamodelSlide: isMetamodelSlideProxy,
  /**
   * @deprecated Read `slides$.byId[slideId]` and apply the matching pure predicate from `@ardoq/api-types`.
   */
  isViewpointSlide,
  /**
   * @deprecated Read `slides$.byId[slideId]` and call `slideOperations.getSort(slide)`.
   */
  getSort,
};
